import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, useApi } from '@/api/client';
import { ScheduleEditor } from '@/components/ScheduleEditor';
import { RecipientsEditor } from '@/components/RecipientsEditor';
import { RecipientConfirmDialog, RecipientMismatchNotice } from '@/components/RecipientWarning';
import { useRecipientCheck } from '@/hooks/useRecipientCheck';
import { ParametersEditor, RunsAsCreatorNotice, SubjectPlaceholderHint } from '@/components/ParametersEditor';
import {
  identityParametersSetTo,
  unsetIdentityParameters,
  upstreamParameterNames,
  withIdentityAccount
} from '@/components/parameters/identityParameter';
import { PickupDestinationChoice } from '@/components/PickupToggle';
import { subscriptionTitle } from '@/types';
import type {
  CreateSubscriptionRequest,
  PickupDeliveryRequest,
  RecipientCheckResult,
  ReportParameterValue,
  SubscriptionRecipients,
  ScheduleDefinition
} from '@/types';
import { HelpLink } from '@/components/HelpLink';

/**
 * The report this form is about to subscribe to.
 *
 * Narrower than the catalogue's own {@link ReportCatalogItem}, which is what the search list hands
 * over, because a clone has no catalogue row to hand over — it knows the report from the
 * subscription it is copying, and the two fields below are the only ones this page ever reads.
 * Widening the state to the catalogue shape would have meant inventing a created date and a
 * `hidden` flag for a report the clone never looked up.
 */
type ChosenReport = { path: string; name: string };

/**
 * Which parameter names name a client, in the order the portal trusts them. The same list the API
 * uses to work out which folder a delivery is filed under — mirrored here only to pre-fill the
 * field, never to decide anything. A wrong guess is corrected by typing over it.
 */
const CLIENT_PARAMETER_NAMES = ['HeadOffice', 'Head Office', 'Client', 'Customer', 'CustomerName', 'HeadOfficeName'];

function guessClientName(parameters: ReportParameterValue[]): string | null {
  for (const candidate of CLIENT_PARAMETER_NAMES) {
    const match = parameters.find(p => p.name.toLowerCase() === candidate.toLowerCase());
    // Single-valued only: a parameter set to twelve head offices does not name a client, and
    // picking the first would file the report under a misleading one.
    if (match && match.values.length === 1 && match.values[0].trim()) return match.values[0];
  }
  return null;
}

/** PBIRS rendering-extension ids; see the same list on the detail page. */
const RENDER_FORMATS = [
  { value: 'EXCELOPENXML', label: 'Excel (.xlsx)' },
  { value: 'PDF', label: 'PDF' },
  { value: 'WORDOPENXML', label: 'Word (.docx)' },
  { value: 'CSV', label: 'CSV' },
  { value: 'MHTML', label: 'Web archive (MHTML)' },
  { value: 'XML', label: 'XML' },
  { value: 'IMAGE', label: 'Image (TIFF)' }
];

/** Same autosize treatment as the detail page's comment field — see that file for why. */
const AUTOSIZE_TEXTAREA_STYLE: CSSProperties = {
  fieldSizing: 'content',
  minHeight: '8em',
  maxHeight: '28em',
  overflowY: 'auto'
};

const defaultSchedule: ScheduleDefinition = {
  frequency: 'Weekly',
  timeOfDay: '09:00:00',
  timeZoneId: 'Europe/London',
  daysOfWeek: 1,
  startDate: null,
  endDate: null,
  intervalDays: null,
  dayOfMonth: null,
  monthInterval: null,
  // Null until the user picks Monthly, which seeds every month. A month set on a weekly
  // schedule would mean nothing.
  months: null,
  cronExpression: null,
  humanReadable: null
};

export function CreateSubscription() {
  const api = useApi();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ChosenReport | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [comment, setComment] = useState('');
  const [renderFormat, setRenderFormat] = useState('EXCELOPENXML');
  // No control of its own on this form — NORMAL is what the API defaults to and what all but a
  // handful of subscriptions on MANDW01 use. It is held as state purely so a clone can carry the
  // source's, rather than quietly demoting a HIGH one on the way through.
  const [priority, setPriority] = useState('NORMAL');
  const [includeReport, setIncludeReport] = useState(true);
  const [includeLink, setIncludeLink] = useState(false);
  const [recipients, setRecipients] = useState<SubscriptionRecipients>({ emails: [], groups: [] });
  const [schedule, setSchedule] = useState<ScheduleDefinition>(defaultSchedule);
  const [parameters, setParameters] = useState<ReportParameterValue[]>([]);
  // Null is "email only", which is what almost every subscription is and therefore the default.
  const [pickup, setPickup] = useState<PickupDeliveryRequest | null>(null);

  const { data: catalog } = useQuery({
    queryKey: ['catalog', search],
    queryFn: () => api.searchCatalog(search || undefined)
  });

  // Already fetched at the app shell and shared through the query cache, so this costs no round
  // trip. Read here for `windowsAccountName`, which is what an identity parameter is answered with.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: Infinity });
  const account = me?.windowsAccountName?.trim() || null;

  // ── Cloning ────────────────────────────────────────────────────────────────────────────
  const cloneFrom = params.get('cloneFrom');

  // The same query key the detail page uses, so arriving here from Clone costs no round trip at
  // all — the subscription is already in the cache, and the form fills in immediately.
  const {
    data: source,
    isFetching: loadingSource,
    error: sourceError
  } = useQuery({
    queryKey: ['sub', cloneFrom],
    queryFn: () => api.getSubscription(cloneFrom!),
    enabled: Boolean(cloneFrom),
    retry: false
  });

  /**
   * The report whose parameter defaults have already been applied.
   *
   * The effect below seeds every parameter from the report's own defaults whenever a report's
   * definitions arrive — which, on a clone, would land a moment after the copied values and
   * overwrite them with the report's, silently discarding the settings that were the reason to
   * clone. Marking the source's path as already seeded is what stops it; picking a different
   * report from the list still re-seeds, because its path is not this one.
   */
  const seededPath = useRef<string | null>(null);

  // Applied once per source. Recipients are deliberately absent: see the notice on the form.
  const [cloned, setCloned] = useState<string | null>(null);
  useEffect(() => {
    if (!source || cloned === source.subscriptionId) return;
    seededPath.current = source.reportPath;
    setSelected({ path: source.reportPath, name: source.reportName });
    setDisplayName(source.displayName ?? '');
    setEmailSubject(source.emailSubject);
    setComment(source.comment);
    setRenderFormat(source.renderFormat);
    setPriority(source.priority);
    setIncludeReport(source.includeReport);
    setIncludeLink(source.includeLink);
    setSchedule(source.schedule);
    // Field-bound parameters (a data-driven subscription's, where the value comes from a column of
    // its query) carry no literal value to copy, so they are left for the report to default rather
    // than written across as empty.
    setParameters(source.parameters
      .filter(p => !p.fromField && p.values.length > 0)
      .map(p => ({ name: p.name, values: p.values })));
    setCloned(source.subscriptionId);
  }, [source, cloned]);

  const {
    data: definitions,
    isFetching: loadingParameters,
    error: parametersError
  } = useQuery({
    queryKey: ['report-parameters', selected?.path],
    queryFn: () => api.reportParameters(selected!.path),
    enabled: Boolean(selected),
    retry: false
  });

  // Start each parameter on what the report itself defaults to, so a subscription created without
  // touching this section behaves the same as running the report by hand.
  //
  // Only the defaults the report PINS. A parameter whose default is an expression is deliberately
  // left unset: the report works that value out on every run — on /Sales/Scope 3 FromDate and
  // ToDate resolve to the ends of the previous month — so copying today's answer in would freeze
  // it, quietly turning "last month" into whichever month someone happened to open this page in.
  // Left unset the report keeps deciding, which is what the live estate does: of the 286
  // subscriptions on MANDW01 only 8 name a date parameter with a value at all. It is also what the
  // default panel's "use the report's default" produces, so the two agree.
  //
  // Skipped for a report whose values are already set — which is only ever a clone, and only for
  // the report it was cloned from. See `seededPath`.
  useEffect(() => {
    if (!definitions || !selected || seededPath.current === selected.path) return;
    seededPath.current = selected.path;
    setParameters(
      definitions
        .filter(d => !d.hasDynamicDefault && d.defaultValues.length > 0)
        .map(d => ({ name: d.name, values: d.defaultValues }))
    );
  }, [definitions, selected]);

  /**
   * Answers the report's identity parameter with the account of the person filling this form in.
   *
   * The failure this exists for, from production: subscriptions the portal created against
   * /Sales/Scope 3 fail every run with "Default value or value provided for the report parameter
   * 'CountryCode' is not a valid value". PBIRS REST creates every subscription as the portal's
   * service account, so at run time the report resolves its User parameter to `KEY\svc_reportportal`
   * and scopes CountryCode's valid values to it — a list with 0 entries, against which any stored
   * country is invalid. The three subscriptions on that report that DO send are the three whose
   * identity resolves to a person. Storing the creator's account is what puts a new one in that
   * group. The user's decision, in their words: "yes auto set as the creators account and save it
   * that way, its their permissions that matter here."
   *
   * A functional update rather than a computed array, because the defaults effect above sets
   * `parameters` in the very same commit that `definitions` arrives in: a value derived from the
   * pre-defaults array would be applied on top of the defaults and silently discard them. Applying
   * it to whatever the queue has produced is order-proof. {@link withIdentityAccount} returns the
   * same array when it has nothing to add, so React bails out and this cannot loop.
   *
   * Not deps-tracked on `parameters` on purpose. Clearing the field by hand is then a choice that
   * sticks, rather than something the form immediately types back over — the seeded note under the
   * field offers it again if that was a mistake.
   */
  useEffect(() => {
    if (!definitions || !account) return;
    setParameters(prev => withIdentityAccount(definitions, prev, account));
  }, [definitions, account]);

  /**
   * The identity parameters this report has that nothing has answered.
   *
   * Empty for every report without one, and empty again the moment the effect above has answered
   * them — so it is non-empty in exactly the case worth stopping for: the report needs to know who
   * it is being run for and Graph could not tell the portal who that is.
   */
  const unansweredIdentity = useMemo(() => {
    const defs = definitions ?? [];
    return unsetIdentityParameters(defs, upstreamParameterNames(defs), parameters);
  }, [definitions, parameters]);

  /**
   * Refuses the create rather than letting it be made and fail at 6am.
   *
   * This is the whole point of the exercise: a subscription that stores no identity value on a
   * report that scopes itself by one is not a subscription with a small gap in it, it is one that
   * never sends and says so only in a status message somebody has to go and look for. A refusal on
   * screen, while the form is still open and the report is still in mind, costs a minute; the
   * alternative costs a morning's report and a support ticket. Narrow on purpose — it can only
   * fire on a report that has an identity parameter AND a caller with no account name, so no other
   * create is affected by it at all.
   */
  const blockedOnAccount = !account && unansweredIdentity.length > 0;

  /** The identity parameters about to be saved as the creator — what the notice reports back. */
  const runsAsParameters = useMemo(
    () => identityParametersSetTo(definitions ?? [], parameters, account),
    [definitions, parameters, account]
  );

  // A parameter names itself; a reader knows it by its prompt. Same fallback as the editor's.
  function parameterLabel(name: string): string {
    const def = (definitions ?? []).find(d => d.name === name);
    return def && def.displayName.trim() !== '' ? def.displayName : name;
  }

  // Set when a create came back refused for an unconfirmed recipient mismatch — see the same
  // handling on the detail page.
  const [pendingConfirm, setPendingConfirm] = useState<RecipientCheckResult | null>(null);

  const create = useMutation({
    mutationFn: (req: CreateSubscriptionRequest) => api.createSubscription(req),
    onSuccess: result => {
      // The subscription exists either way — the pickup companion is a second subscription on
      // MANDW01 that is allowed to fail on its own, and in production right now it does, because
      // the storage account is unreachable. Navigating to it silently was the page asserting that
      // file delivery is in place when it is not; the subscription's own page is where it is said,
      // because that is where the retry lives.
      const pickupMissing = result.pickup === 'Failed' || result.pickup === 'Unconfigured';
      navigate(
        `/subscriptions/${result.id}`,
        pickupMissing
          ? { state: { pickupSetupProblem: { outcome: result.pickup, problem: result.pickupProblem ?? null } } }
          : undefined
      );
    },
    onError: (e: Error) => {
      if (e instanceof ApiError && e.code === 'recipient.mismatch_unconfirmed' && e.data) {
        setPendingConfirm(e.data as RecipientCheckResult);
      }
    }
  });

  const recipientCheck = useRecipientCheck(recipients, parameters, Boolean(selected));

  function handleCreate(confirmRecipientMismatch?: string | null) {
    if (!selected || blockedOnAccount) return;
    create.mutate({
      reportPath: selected.path,
      emailSubject: emailSubject || selected.name,
      comment,
      renderFormat,
      recipients,
      schedule,
      parameters,
      priority,
      includeReport,
      includeLink,
      confirmRecipientMismatch,
      // Blank means "no name of its own": the report server then generates its usual description
      // rather than storing an empty one.
      displayName: displayName.trim() || null,
      // Omitted rather than sent as `{ enabled: false }` so an email-only create is byte-for-byte
      // the request it has always been.
      pickupDelivery: pickup ?? undefined
    });
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{cloneFrom ? 'Clone subscription' : 'New subscription'}</h1>
          <div className="sub">
            {cloneFrom
              ? 'Everything from the original except its recipients, which you choose below.'
              : 'Schedule a Power BI Report Server report, and choose where each run goes — by email, '
                + 'to report pickup, or both.'}
            {' '}<HelpLink slug={cloneFrom ? 'clone-for-another-client' : 'schedule-a-report'} />
          </div>
        </div>
      </div>

      {sourceError && (
        <div className="error-banner">
          The subscription being cloned could not be read, so nothing has been filled in:{' '}
          {(sourceError as Error).message.replace(/^API \d+: /, '')}
        </div>
      )}

      {cloneFrom && !sourceError && (loadingSource && !source ? (
        <div className="placeholder">Reading the subscription being cloned...</div>
      ) : source ? (
        <div className="notice notice-warn" style={{ marginBottom: 18 }}>
          <strong>Recipients have not been copied — that is deliberate.</strong>
          <div>
            The report, name, subject, message, format, parameters and schedule all came from{' '}
            <strong>{subscriptionTitle(source)}</strong>, but a clone almost always exists to send
            the same report to somebody else. Add the new recipients in section 5; nothing can be
            created until you do.
            {source.isDataDriven && (
              <> The original is data-driven, so its query-supplied recipients and parameter values
                could not be carried over at all — this clone will be an ordinary fixed one.</>
            )}
            {source.uneditableScheduleReason && (
              <> Check the schedule before creating: the original {source.uneditableScheduleReason},
                so what is shown in section 4 is a simplified version of it.</>
            )}
            {source.pickup && source.pickup.health !== 'None' && (
              <> The original also files to report pickup. That has <strong>not</strong> been
                switched on here — it creates a second subscription on the report server, so it is
                left for you to choose in section 5, where the client name is already filled in.</>
            )}
          </div>
        </div>
      ) : null)}

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header"><h3>1. Choose a report</h3></div>
        <div className="card-body">
          {/* Stated outright rather than left to a highlighted row in the list below: a clone's
              report is chosen before the list has been searched, and may not be in the first page
              of it at all. */}
          {selected && (
            <div className="notice" style={{ border: '1px solid var(--kt-grey-200)', marginBottom: 14 }}>
              <strong>{selected.name}</strong>
              <div style={{ fontSize: '.8rem', color: 'var(--kt-grey-400)', fontFamily: 'ui-monospace, Consolas, monospace' }}>
                {selected.path}
              </div>
            </div>
          )}
          <input
            className="input"
            style={{ width: '100%' }}
            type="search"
            placeholder="Search the catalogue..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={{ marginTop: 14, border: '1px solid var(--kt-grey-200)', borderRadius: 'var(--radius-sm)', maxHeight: 220, overflow: 'auto' }}>
            {(catalog ?? []).map(item => (
              <div
                key={item.path}
                style={{
                  padding: '12px 14px',
                  borderBottom: '1px solid var(--kt-grey-100)',
                  background: selected?.path === item.path ? 'var(--kt-teal-bg)' : undefined,
                  cursor: 'pointer'
                }}
                onClick={() => setSelected(item)}>
                <div style={{ fontWeight: 600, color: 'var(--kt-green-dark)' }}>{item.name}</div>
                <div style={{ fontSize: '.8rem', color: 'var(--kt-grey-400)', fontFamily: 'ui-monospace, Consolas, monospace' }}>{item.path}</div>
              </div>
            ))}
            {(catalog ?? []).length === 0 && (
              <div className="placeholder">No reports match.</div>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header"><h3>2. Name and email</h3></div>
        <div className="card-body">
          <div className="field">
            <label>Name (optional)</label>
            <input
              className="input"
              value={displayName}
              placeholder={selected?.name ?? ''}
              onChange={e => setDisplayName(e.target.value)}
            />
            <div className="field-hint">
              What this subscription is called in lists and headings — worth setting when the same
              report is subscribed to several times over for different clients. Leave it blank and
              the report name is used instead.
            </div>
          </div>
          <div className="field">
            <label>Subject</label>
            <input className="input" value={emailSubject} placeholder={selected?.name ?? ''} onChange={e => setEmailSubject(e.target.value)} />
            <SubjectPlaceholderHint parameterNames={(definitions ?? []).map(d => d.name)} />
          </div>
          <div className="field">
            <label>Comment / message body</label>
            <textarea
              className="input"
              rows={4}
              value={comment}
              onChange={e => setComment(e.target.value)}
              style={AUTOSIZE_TEXTAREA_STYLE}
            />
          </div>
          <div className="field">
            <label>Render format</label>
            <select className="select" value={renderFormat} onChange={e => setRenderFormat(e.target.value)}>
              {RENDER_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="checkbox-field">
              <input type="checkbox" checked={includeReport} onChange={e => setIncludeReport(e.target.checked)} />
              Attach the report to the email
            </label>
            <label className="checkbox-field">
              <input type="checkbox" checked={includeLink} onChange={e => setIncludeLink(e.target.checked)} />
              Include a link to the report on the report server
            </label>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header"><h3>3. Report parameters</h3></div>
        <div className="card-body">
          {!selected ? (
            <div className="placeholder">Choose a report first.</div>
          ) : loadingParameters ? (
            <div className="placeholder">Reading the report's parameters...</div>
          ) : parametersError ? (
            // Not the same as "no parameters": saying that would hide parameters the report does
            // have, and the subscription would then run on whatever the report defaults to.
            <div className="error-banner">
              The report's parameters could not be read, so they cannot be set here.
              The subscription will run with whatever the report defaults to.
            </div>
          ) : (
            <>
              {/* Above the fields, not below them: this says what the section as a whole is about
                  to save, and one of the two says the section cannot be saved at all. */}
              {blockedOnAccount ? (
                <div className="error-banner">
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    Your Windows account name could not be looked up, and this report needs it.
                  </div>
                  <div>
                    <strong>{unansweredIdentity.map(parameterLabel).join(', ')}</strong> is what
                    tells this report whose data to render. Left unset, the report server works it
                    out at run time and gets the portal&rsquo;s own service account, which belongs
                    to no client and can see nothing — the subscription would be created and then
                    fail every single run without sending anything, which is why creating is
                    blocked here instead. If the field below will let you, type your account name
                    into it (the short form, like <code>dmorris</code>) and this clears. If it will
                    not, your Entra account carries no Active Directory account name for the portal
                    to read: ask IT to check that, or create this one in Report Server Schedule
                    Manager.
                  </div>
                </div>
              ) : account ? (
                <RunsAsCreatorNotice
                  account={account}
                  parameterLabels={runsAsParameters.map(parameterLabel)}
                />
              ) : null}
              <ParametersEditor
                definitions={definitions ?? []}
                values={parameters}
                onChange={setParameters}
                reportPath={selected?.path}
              />
            </>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header"><h3>4. Schedule</h3></div>
        <div className="card-body">
          <ScheduleEditor value={schedule} onChange={setSchedule} />
        </div>
      </div>

      {/* Recipients and pickup in one section on purpose. Report pickup is a destination alongside
          the people, not a different kind of subscription — folding it in here is what makes it
          something you choose while deciding who gets the report, rather than a mode switch at the
          top of the form that reframes everything below it. */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header"><h3>5. Recipients and delivery</h3></div>
        <div className="card-body">
          {/* Said again here, next to the empty list, because the notice at the top of a long form
              is off screen by the time anyone reaches this section and an empty recipients box
              otherwise reads as something that failed to load. */}
          {cloned && (
            <div className="field-hint" style={{ marginTop: 0, marginBottom: 10 }}>
              Starting empty: the original&rsquo;s recipients were not copied.
            </div>
          )}
          <RecipientsEditor value={recipients} onChange={setRecipients} />
          <RecipientMismatchNotice check={recipientCheck} />

          <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--kt-grey-200)' }}>
            <div style={{ fontWeight: 600, color: 'var(--kt-green-dark)', marginBottom: 8 }}>
              Report pickup
            </div>
            {/* A clone offers the original's client rather than a guess from the parameters, but
                only as the name to use IF pickup is switched on — the checkbox stays off, because
                turning it on creates a second subscription on the report server and that is not
                something to do on the user's behalf while they are looking elsewhere. */}
            <PickupDestinationChoice
              value={pickup}
              onChange={setPickup}
              suggestedClientName={source?.pickup?.clientName ?? guessClientName(parameters)}
            />
          </div>
        </div>
      </div>

      {/* The mismatch refusal has its own dialog; showing it here too would report a failure for
          something the page is already asking a question about. */}
      {create.error && !pendingConfirm && (
        <div className="error-banner" style={{ marginBottom: 14 }}>
          {(create.error as Error).message.replace(/^API \d+: /, '')}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn-secondary" onClick={() => navigate('/subscriptions')}>Cancel</button>
        {/* Disabled rather than hidden, and never without the reason being on screen already: the
            account refusal explains itself in section 3, which is also where it can be resolved. */}
        <button
          className="btn-primary"
          onClick={() => handleCreate()}
          disabled={!selected || create.isPending || blockedOnAccount
            || (recipients.emails.length === 0 && recipients.groups.length === 0)}
          title={blockedOnAccount
            ? 'This report needs to know which account it runs as — see Report parameters above'
            : undefined}
        >
          {create.isPending ? 'Creating...' : 'Create subscription'}
        </button>
      </div>

      {pendingConfirm && (
        <RecipientConfirmDialog
          check={pendingConfirm}
          busy={create.isPending}
          onConfirm={() => handleCreate(pendingConfirm.fingerprint)}
          onCancel={() => { setPendingConfirm(null); create.reset(); }}
        />
      )}
    </>
  );
}
